import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Users, Clock, TrendingUp, CalendarDays, Timer, Award } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const PIE_COLORS = ['#002FA7', '#16A34A', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#EF4444', '#84CC16'];

const CHART_MARGIN = { top: 5, right: 10, left: -10, bottom: 5 };
const AXIS_TICK = { fill: '#94A3B8', fontSize: 11 };
const AXIS_LINE = { stroke: '#E2E8F0' };
const TOOLTIP_STYLE = { border: '1px solid #E2E8F0', borderRadius: '2px', fontSize: '12px', fontFamily: 'Inter' };

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="border border-border bg-card rounded-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] text-muted-foreground uppercase tracking-widest font-body">{label}</span>
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </div>
      </div>
      <p className="text-2xl font-heading font-bold text-foreground tracking-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1 font-body">{sub}</p>}
    </div>
  );
}

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/analytics/attendance')
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-muted-foreground">Loading analytics...</div>;
  if (!data) return <div className="text-sm text-muted-foreground">No data available</div>;

  return (
    <div data-testid="analytics-page">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Attendance Analytics</h2>
        <p className="text-sm text-muted-foreground font-body mt-1">Overview of attendance trends and statistics for {data.year}</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Total Employees" value={data.employee_count} color="bg-blue-50 text-[#002FA7]" />
        <StatCard icon={Clock} label="Present Today" value={data.today_present} sub={`${data.today_rate}% attendance rate`} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={Timer} label="Hours This Month" value={`${data.month_total_hours}h`} sub={`${data.month_total_checkins} check-ins`} color="bg-amber-50 text-amber-600" />
        <StatCard icon={CalendarDays} label="Leaves" value={data.month_approved_leaves} sub={`${data.pending_leaves} pending`} color="bg-violet-50 text-violet-600" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Monthly Attendance Chart */}
        <div className="lg:col-span-2 border border-border bg-card rounded-sm p-5">
          <h3 className="font-heading font-semibold text-foreground mb-1">
            <TrendingUp className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
            Monthly Check-ins
          </h3>
          <p className="text-xs text-muted-foreground mb-4">Total attendance records per month in {data.year}</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={AXIS_TICK} axisLine={AXIS_LINE} />
                <YAxis tick={AXIS_TICK} axisLine={AXIS_LINE} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value, name) => [value, name === 'check_ins' ? 'Check-ins' : 'Working Days']}
                  labelFormatter={(label) => `${label} ${data.year}`}
                />
                <Bar dataKey="check_ins" fill="#002FA7" radius={[2, 2, 0, 0]} name="Check-ins" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Department Distribution */}
        <div className="border border-border bg-card rounded-sm p-5">
          <h3 className="font-heading font-semibold text-foreground mb-1">
            <Users className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
            By Department
          </h3>
          <p className="text-xs text-muted-foreground mb-4">Employee distribution</p>
          {data.departments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No departments</p>
          ) : (
            <>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.departments}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      dataKey="count"
                      nameKey="name"
                      strokeWidth={2}
                      stroke="#fff"
                    >
                      {data.departments.map((dept, i) => (
                        <Cell key={dept.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ border: '1px solid #E2E8F0', borderRadius: '2px', fontSize: '12px' }}
                      formatter={(value) => [`${value} employees`]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 mt-2">
                {data.departments.map((dept, i) => (
                  <div key={dept.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-muted-foreground font-body">{dept.name}</span>
                    </div>
                    <span className="font-mono text-xs text-muted-foreground">{dept.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Top Attendees */}
      <div className="border border-border bg-card rounded-sm p-5">
        <h3 className="font-heading font-semibold text-foreground mb-1">
          <Award className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
          Top Attendees This Month
        </h3>
        <p className="text-xs text-muted-foreground mb-4">Employees with most attendance days this month</p>
        {data.top_attendees.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No attendance data this month</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {data.top_attendees.map((emp, i) => (
              <div key={emp.employee_id} className="border border-border rounded-sm p-3 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-sm flex items-center justify-center text-white text-xs font-bold font-heading shrink-0 ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : 'bg-orange-400'}`}>
                  {i + 1}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{emp.name}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground font-mono">{emp.employee_id}</span>
                    <Badge variant="secondary" className="text-[10px] rounded-sm">{emp.days_present}d</Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
